import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import AddIcon from "@mui/icons-material/AddOutlined";
import { DataTable, type Column } from "@/components/data/data-table";
import { MonoText } from "@/components/display/mono";
import { EmptyState } from "@/components/feedback/states";
import { IngestEndpointDialog } from "@/components/endpoints/ingest-endpoint-dialog";
import { type IngestEndpoint, useIngestEndpoints } from "@/api/endpoints";

export const Route = createFileRoute("/_app/endpoints/ingest/")({
  component: IngestList,
});

function IngestList() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useIngestEndpoints();
  const [creating, setCreating] = useState(false);

  const columns: Column<IngestEndpoint>[] = [
    { id: "name", header: "Name", cell: (r) => r.name, sortValue: (r) => r.name },
    { id: "slug", header: "Slug", cell: (r) => <MonoText>{r.slug}</MonoText>, nowrap: true },
    { id: "format", header: "Format", cell: (r) => r.format ?? "JSON", nowrap: true },
    { id: "target", header: "Target", cell: (r) => r.targetType ?? "—", nowrap: true },
    { id: "cols", header: "Columns", align: "right", cell: (r) => r.schema?.length ?? 0 },
  ];

  return (
    <Card>
      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        error={error}
        onRetry={() => refetch()}
        rowKey={(r) => r.id}
        search={(r) => `${r.name ?? ""} ${r.slug ?? ""}`}
        searchPlaceholder="Search endpoints…"
        toolbar={
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
          >
            New endpoint
          </Button>
        }
        onRowClick={(r) => navigate({ to: "/endpoints/ingest/$id", params: { id: r.id } })}
        empty={
          <EmptyState
            title="No ingest endpoints"
            description="Create a vendor-facing endpoint so partners can push typed data in."
          />
        }
      />
      <IngestEndpointDialog open={creating} onClose={() => setCreating(false)} />
    </Card>
  );
}
